import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body1, Body5, Body7, Container } from '@ui';
import { CopyButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { ChipRow } from '@features/feedView/FilterChip';
import { DefList } from './DefList';
import { CardRow, HeaderCell, HeaderRow, Mono, PassiveCard, Row, SectionHead, Table, TableScroll } from './styles';
import { fmdcPaths, queueUrl } from './fmdcUrl';
import { formatBytes, formatCount, formatDateTime, formatLoadAction, formatReviewReason, EM_DASH } from './format';

// The Overview tab: what the run produced, in numbers, and the ways into the queue.

const CountValue = styled.div`
  margin-top: 4px;
`;

const countCards = (counts = {}) => [
  { label: 'Model cells', value: formatCount(counts.model_cells) },
  { label: 'Extraction rows', value: formatCount(counts.extraction_rows) },
  {
    label: 'Unmapped rows · cells',
    value: `${formatCount(counts.unmapped_rows)} · ${formatCount(counts.unmapped_cells)}`,
  },
  { label: 'Proposals', value: formatCount(counts.proposals) },
  { label: 'LLM calls · tokens', value: `${formatCount(counts.llm_calls)} · ${formatCount(counts.llm_tokens)}` },
];

// `load_actions` on the wire is a count per action; an array of `{ load_action, count }` is read
// the same way so a shape change on the server does not blank the section.
export const loadActionRows = (loadActions) => {
  if (!loadActions) return [];
  if (Array.isArray(loadActions)) {
    return loadActions.map((entry) => ({
      label: formatLoadAction(entry.load_action || entry.action || entry.key),
      value: formatCount(entry.count ?? entry.rows ?? entry.value),
    }));
  }
  return Object.entries(loadActions).map(([action, count]) => ({
    label: formatLoadAction(action),
    value: formatCount(count),
  }));
};

const SHEET_NAME_KEYS = ['sheet', 'name', 'sheet_name', 'tab', 'title'];

// The sheet inventory's shape is not pinned by the handoff. An array of objects, an object keyed
// by sheet name, or a list of names all come out as `{ name, fields: [{ label, value }] }`, with
// every numeric field shown and the name taken from whichever key carries it.
// `sheet_inventory` is an object keyed by sheet name: { state, header_row, label_col, section_col,
// hidden_rows, hidden_cols, numeric_cells, date_cells, formula_cells, error_cells,
// numeric_string_cells, na_marker_cells, fm_rows, max_row, inventoried }; a skipped sheet (a
// chartsheet, say) is { state, skipped: <reason> }. The counts worth a glance come first, in a
// fixed order; anything else numeric follows so a new field is not lost.
const SHEET_FIELDS = [
  ['max_row', 'rows'],
  ['fm_rows', 'model rows'],
  ['numeric_cells', 'numeric'],
  ['formula_cells', 'formulas'],
  ['date_cells', 'dates'],
  ['error_cells', 'errors'],
  ['na_marker_cells', 'n/a markers'],
  ['hidden_rows', 'hidden rows'],
  ['hidden_cols', 'hidden cols'],
];

export const normalizeSheetInventory = (inventory) => {
  if (!inventory) return [];
  const toEntry = (name, data) => {
    if (!data || typeof data !== 'object') return { name: String(name), fields: [] };
    const fields = [];
    if (data.skipped) fields.push({ label: 'skipped', value: String(data.skipped) });
    else if (data.inventoried === false) fields.push({ label: 'not inventoried', value: '' });
    if (data.state && data.state !== 'visible') fields.push({ label: 'state', value: String(data.state) });
    const seen = new Set(['skipped', 'inventoried', 'state', ...SHEET_NAME_KEYS]);
    SHEET_FIELDS.forEach(([key, label]) => {
      // A zero count teaches nothing on a row of counts, so only non-zero ones are listed.
      if (typeof data[key] === 'number') {
        seen.add(key);
        if (data[key] > 0) fields.push({ label, value: formatCount(data[key]) });
      }
    });
    Object.entries(data)
      .filter(([key, value]) => !seen.has(key) && typeof value === 'number' && value > 0 && !/_col$|_row$/.test(key))
      .forEach(([key, value]) => fields.push({ label: key.replace(/_/g, ' '), value: formatCount(value) }));
    return { name: String(name), fields };
  };
  if (Array.isArray(inventory)) {
    return inventory.map((entry, index) => {
      if (typeof entry !== 'object' || entry === null) return { name: String(entry), fields: [] };
      const nameKey = SHEET_NAME_KEYS.find((key) => entry[key] !== undefined);
      return toEntry(nameKey ? entry[nameKey] : `Sheet ${index + 1}`, entry);
    });
  }
  if (typeof inventory === 'object') {
    return Object.entries(inventory).map(([name, data]) => toEntry(name, data));
  }
  return [];
};

const SHEET_COLUMNS = 'minmax(160px, 1.4fr) minmax(200px, 3fr)';

export const RunOverview = ({ run }) => {
  const navigate = useNavigate();
  const byReason = run?.review?.by_reason || {};
  const reasons = Object.entries(byReason).filter(([, count]) => count > 0);
  const sheets = normalizeSheetInventory(run?.sheet_inventory);
  const loadActions = loadActionRows(run?.validation_summary?.load_actions);
  const source = run?.source || {};

  const openReason = (reason) => {
    const query = queueUrl.serialize({ ...queueUrl.defaults, reason }).toString();
    navigate(`${fmdcPaths.review(ROUTES.fmdc, run.id)}?${query}`);
  };

  const sourceRows = [
    { label: 'Source name', value: source.source_name || run?.source_name },
    {
      label: 'SHA-256',
      value: source.sha256 ? (
        <Container row verticallyCenter flexWrap gap={8}>
          <Mono>{source.sha256}</Mono>
          <CopyButton text={source.sha256} transparent />
        </Container>
      ) : null,
    },
    { label: 'File size', value: source.file_size_bytes != null ? formatBytes(source.file_size_bytes) : null },
    { label: 'Channel', value: source.channel },
    { label: 'Received', value: source.received_at ? formatDateTime(source.received_at) : null },
  ];

  return (
    <div>
      <CardRow>
        {countCards(run?.counts).map((card) => (
          <PassiveCard key={card.label}>
            <Body7 isLowContrast>{card.label}</Body7>
            <CountValue>
              <Body1 bold>{card.value}</Body1>
            </CountValue>
          </PassiveCard>
        ))}
      </CardRow>

      <Container top={24}>
        <SectionHead heading="Review by reason" subtext="Press a reason to open the queue narrowed to it." />
        <Container top={12}>
          {reasons.length === 0 ? (
            <Body7 isLowContrast>Nothing is waiting for review.</Body7>
          ) : (
            <ChipRow>
              {reasons.map(([reason, count]) => (
                <SkeletonButton
                  key={reason}
                  onClick={() => openReason(reason)}
                  buttonProps={{ 'aria-label': `Open the review queue for ${formatReviewReason(reason)}` }}
                >
                  {formatReviewReason(reason)} ({formatCount(count)})
                </SkeletonButton>
              ))}
            </ChipRow>
          )}
        </Container>
      </Container>

      <Container top={24}>
        <SectionHead heading="Load actions" subtext="What each extracted value will do when the run is loaded." />
        <Container top={12}>
          {loadActions.length === 0 ? <Body7 isLowContrast>{EM_DASH}</Body7> : <DefList rows={loadActions} />}
        </Container>
      </Container>

      <Container top={24}>
        <SectionHead heading="Sheets" />
        <Container top={12}>
          {sheets.length === 0 ? (
            <Body7 isLowContrast>No sheet inventory was recorded.</Body7>
          ) : (
            <TableScroll>
              <Table $minWidth={420}>
                <HeaderRow $columns={SHEET_COLUMNS}>
                  <HeaderCell>Sheet</HeaderCell>
                  <HeaderCell>Counts</HeaderCell>
                </HeaderRow>
                {sheets.map((sheet) => (
                  <Row key={sheet.name} $columns={SHEET_COLUMNS} $interactive={false}>
                    <Body5 bold>{sheet.name}</Body5>
                    <Body7 isLowContrast>
                      {sheet.fields.length === 0
                        ? EM_DASH
                        : sheet.fields
                            .map((field) => (field.value ? `${field.label}: ${field.value}` : field.label))
                            .join(' · ')}
                    </Body7>
                  </Row>
                ))}
              </Table>
            </TableScroll>
          )}
        </Container>
      </Container>

      <Container top={24}>
        <SectionHead heading="Source" />
        <Container top={12}>
          <DefList rows={sourceRows} />
        </Container>
      </Container>
    </div>
  );
};
